// Canonical VALID fixtures - one per top-level schema. Round-trip tests parse these; downstream
// packages (D1 data authoring, engine tests) can reuse them as known-good instances. Every fixture
// is annotated with its inferred type so `tsc` proves the fixture matches the schema at build time.
import { CardIndexSchema } from './primitives';
/** brand a raw int as a CardIndex (also validates 0..51). */
export const ci = (n) => CardIndexSchema.parse(n);
export const validBoard = [ci(51), ci(47), ci(3)];
const heroHole = [ci(50), ci(46)];
export const validAction = { seat: 3, kind: 'raise', amount: 25, sizingIntent: 'twoThirds' };
export const validEquityResult = { equity: 0.62, ci95: 0.01, win: 0.6, tie: 0.02 };
export const validImbalanceProfile = {
    bettingVolume: 'overCalls',
    elasticity: 'inelastic',
    polarity: 'tooMerged',
    boardCoverage: 'normal',
    equityMgmt: 'overvaluesPairs',
};
const stickerActionModel = {
    continueProb: { top_pair: 0.95, second_pair: 0.9, air: 0.4 },
    raiseProb: { set: 0.6, two_pair: 0.4, air: 0.05 },
    bluffWeight: 0.3,
    sizingHabits: [{ context: 'flop_cbet', intent: 'half', prob: 0.7 }],
};
export const validArchetype = {
    id: 'sticker',
    label: 'The Sticker',
    description: 'Calls too much and hates folding any pair.',
    signals: ['limps in early', 'calls flop and turn with third pair'],
    imbalances: validImbalanceProfile,
    observance: 0.2,
    preflop: {
        rangeSetId: 'live_loose_9max',
        vpipScale: 1.6,
        threeBetScale: 0.5,
        limpBias: 0.7,
        openSizeBB: [4, 6],
    },
    postflop: stickerActionModel,
    sizingTells: [{ pattern: 'donkLarge', meaning: 'valueHeavy', confidence: 'medium' }],
};
export const validWeightedRange = {
    weights: new Float32Array(1326),
    removed: new Uint16Array([0, 1, 2]),
};
export const validBucketedRange = {
    buckets: {
        value: { combos: 40, pctOfRange: 25, top: [['As', 'Ks']] },
        strongDraw: { combos: 12, pctOfRange: 7.5, top: [['Jh', 'Th']] },
        weakDraw: { combos: 20, pctOfRange: 12.5, top: [['9h', '8h']] },
        showdown: { combos: 30, pctOfRange: 18.75, top: [['Ad', 'Qc']] },
        air: { combos: 48, pctOfRange: 30, top: [['7c', '2d']] },
        trap: { combos: 10, pctOfRange: 6.25, top: [['8c', '8d']] },
    },
};
export const validBoardEvent = {
    kind: 'overcard',
    rankClass: 'ace',
    completesFlush: false,
    pairsBoard: false,
};
export const validScenarioSpec = {
    gameType: 'cash',
    tableSize: 9,
    stakes: { sb: 1, bb: 3, currency: 'USD' },
    heroSeat: 5,
    heroPosition: 'CO',
    stackDepthBB: 150,
    villains: [
        { seat: 2, archetypeId: 'sticker' },
        {
            seat: 7,
            archetypeId: 'rock',
            blend: [
                { archetypeId: 'rock', weight: 0.7 },
                { archetypeId: 'waitingRoom', weight: 0.3 },
            ],
        },
    ],
    spotWeights: { srp_ip_cbet: 2, river_bluffcatch: 1 },
    seed: 'seed-abc-123',
};
export const validPlayerState = {
    seat: 5,
    position: 'CO',
    stack: 450,
    committedThisStreet: 0,
    committedTotal: 3,
    folded: false,
    allIn: false,
    holeCards: heroHole,
    archetypeId: undefined,
};
export const validHandState = {
    handId: 'hand-1',
    spec: validScenarioSpec,
    street: 'flop',
    board: validBoard,
    players: [validPlayerState, { ...validPlayerState, seat: 2, position: 'HJ', holeCards: undefined, archetypeId: 'sticker' }],
    buttonSeat: 6,
    pots: [{ amount: 24, eligibleSeats: [2, 5] }],
    currentBet: 0,
    minRaiseTo: 6,
    toAct: 5,
    history: [{ street: 'preflop', action: validAction }],
    phase: 'acting',
};
export const validDecisionPoint = {
    handId: 'hand-1',
    street: 'flop',
    decisionIndex: 0,
    state: validHandState,
    legal: [{ kind: 'check' }, { kind: 'bet', min: 4, max: 450 }],
    pot: 24,
    toCall: 0,
    effectiveStack: 447,
    spr: 18.6,
};
export const validRecommendedAction = {
    baselineAction: 'check',
    finalAction: 'bet',
    action: 'bet',
    sizingIntent: 'twoThirds',
    targetStatement: 'bet for thin value; the Sticker calls with worse pairs',
    firedRules: [
        {
            id: 'OVERCALLER_THIN_VALUE',
            principle: 'Against players who call too much, bet thinner for value and size up.',
            villainNote: 'The Sticker hates folding pairs.',
            confidence: 'high',
            framework: 'five-imbalances',
        },
    ],
    alternatives: [{ action: 'check', evGapBucket: 'clear' }],
};
export const validUserAction = {
    action: 'bet',
    sizingIntent: 'twoThirds',
    typedAmount: 16,
    arithmeticVerdict: 'exact',
    sizingFit: 'fits',
    grade: 9,
    gradeBand: 'good',
    evLossBB: 0.1,
    skillScores: { value_extraction: 9 },
    errorCodes: [],
    firedRuleIds: ['OVERCALLER_THIN_VALUE'],
};
export const validHeroImageState = {
    handsObserved: 22,
    foldToAggressionPct: 35,
    shownBluffs: 1,
    openFreqPct: 24,
};
export const validEngineFacts = {
    v: 1,
    handId: 'hand-1',
    street: 'flop',
    decisionIndex: 0,
    context: {
        gameType: 'cash',
        mode: 'guided',
        position: 'CO',
        positionClass: 'IP',
        playersInHand: 2,
        icmPressure: undefined,
    },
    math: {
        pot: 24,
        toCall: 0,
        effectiveStack: 447,
        spr: 18.6,
        sprBucket: 'over13',
        potOdds: '-',
        requiredEquityPct: 0,
    },
    hero: {
        holeCards: heroHole,
        handClass: 'second_pair_good_kicker',
        drawFlags: ['backdoor_flush'],
        equity: validEquityResult,
        equityBucket: 'ahead',
        equityDelta: 4,
        equityDeltaBucket: 'held',
    },
    board: { cards: validBoard, textureClass: 'dry', streetEvent: validBoardEvent },
    villain: {
        seat: 2,
        archetypeId: 'sticker',
        imbalances: validImbalanceProfile,
        rangeShape: { valueHeavyPct: 55, bluffPct: 8, topClasses: ['top pairs', 'weak pairs'] },
        bucketed: validBucketedRange,
    },
    heroPerceived: { summary: ['strong made hands'], capped: false, image: validHeroImageState },
    recommendation: validRecommendedAction,
    whatChanged: {
        equityDeltaBucket: 'held',
        textureEvents: [validBoardEvent],
        rangeAdvantageShift: 'toHero',
        lines: ['Flop is dry; your second pair is ahead of a Sticker range.'],
    },
    icm: undefined,
    userAction: validUserAction,
};
export const validFactsDigest = {
    v: 1,
    street: 'flop',
    context: {
        gameType: 'cash',
        mode: 'guided',
        positionClass: 'IP',
        position: 'CO',
        playersInHand: 2,
        icmPressure: undefined,
    },
    sprBucket: 'over13',
    priceBucket: undefined,
    hero: {
        handClass: 'second_pair_good_kicker',
        drawFlags: ['backdoor_flush'],
        equityBucket: 'ahead',
        equityDeltaBucket: 'held',
    },
    board: { textureClass: 'dry', streetEvent: validBoardEvent },
    villain: {
        archetypeId: 'sticker',
        imbalances: validImbalanceProfile,
        rangeShape: { valueHeavyPct: 55, bluffPct: 8, topClasses: ['top pairs'] },
    },
    heroPerceived: { summary: ['strong made hands'], capped: false },
    recommendation: {
        action: 'bet',
        sizingIntent: 'twoThirds',
        firedRules: [
            {
                id: 'OVERCALLER_THIN_VALUE',
                principle: 'Against players who call too much, bet thinner for value and size up.',
                confidence: 'high',
            },
        ],
        alternatives: [{ action: 'check', evGapBucket: 'clear' }],
    },
    userAction: { action: 'bet', sizingIntent: 'twoThirds', arithmeticVerdict: 'exact', sizingFit: 'fits', gradeBand: 'good' },
};
export const validCoachText = {
    beginner_thought: 'I have a pair, I guess I bet?',
    pro_thought: 'The Sticker calls worse pairs, so this is a thin value bet, not a check-back.',
    what_changed: undefined,
    villain_read: 'He will not fold second pair here, so charge him.',
};
export const validCoachProvenance = {
    model: 'claude-haiku-4-5',
    promptVersion: 'coach:v1',
    createdAt: 1751990400000,
};
export const validResultRecord = {
    winners: [5],
    potsAwarded: [{ amount: 56, seats: [5] }],
    showdown: [{ seat: 5, holeCards: heroHole, handRank: 1234 }],
};
export const validDebriefRecord = {
    biggestMistake: 'Checked the turn instead of barreling.',
    bestDecision: 'Thin value bet the flop.',
    altLines: ['bet-bet-bet'],
    summary: 'Solid value line; one missed bet on the turn.',
};
export const validHandRecord = {
    spec: validScenarioSpec,
    seed: 'seed-abc-123',
    actions: [validAction, { seat: 2, kind: 'call', amount: 25 }],
    board: validBoard,
    holeCards: heroHole,
    result: validResultRecord,
    perDecisionFacts: [validEngineFacts],
    source: 'generated',
};
export const validDecisionRecord = {
    id: 'dec-1',
    handId: 'hand-1',
    userId: 'user-1',
    street: 'flop',
    decisionIndex: 0,
    position: 'CO',
    villainArchetype: 'sticker',
    stackDepthBucket: '150bb',
    tournamentStage: undefined,
    engineFacts: validEngineFacts,
    userAction: validUserAction,
    grade: 9,
    evLossBB: 0.1,
    skillScores: { value_extraction: 9 },
    errorCodes: [],
    firedRuleIds: ['OVERCALLER_THIN_VALUE'],
    createdAt: 1751990400000,
};
export const validSessionRecord = {
    id: 'sess-1',
    userId: 'user-1',
    mode: 'guided',
    gameType: 'cash',
    stakesLabel: '$1/$3',
    imageState: validHeroImageState,
    startedAt: 1751990000000,
    endedAt: 1751993600000,
};
export const validCoachRequest = {
    factsDigest: validFactsDigest,
    slots: ['beginner_thought', 'pro_thought', 'villain_read'],
};
export const validCoachResponse = {
    text: validCoachText,
    provenance: validCoachProvenance,
    cached: false,
};
export const validPersistHandRequest = {
    sessionId: 'sess-1',
    handRecord: validHandRecord,
    overallGrade: 8.5,
    debrief: validDebriefRecord,
};
export const validPersistDecisionsRequest = {
    decisions: [validDecisionRecord],
};
export const validProgressDashboard = {
    totals: { hands: 120, decisions: 480 },
    gradeByStreet: { flop: 7.2, turn: 6.4 },
    gradeByArchetype: { sticker: 7.8 },
    gradeByStackDepth: { '150bb': 7.1 },
    skillDimensions: [{ dimension: 'value_extraction', avg: 7.9, n: 60 }],
    leaks: [{ code: 'BLUFFED_STATION', statement: 'You are over-bluffing calling stations', leakScore: 42.5, count: 17 }],
    improvements: ['You are improving in fold discipline'],
    bestSkill: 'value_extraction',
    biggestLeak: 'BLUFFED_STATION',
};
export const validImportRequest = {
    text: 'PokerStars Hand #123: ...',
    format: 'pokerstars',
};
// ── progress: cadence knob + session/series debrief (S15) ──────────────────────
export const validDecisionRef = {
    handId: 'hand-7',
    decisionIndex: 2,
    street: 'river',
    grade: 3,
    note: 'You are over-bluffing calling stations',
};
export const validDebriefUnit = {
    scope: 'hand',
    key: 'hand-7',
    handIds: ['hand-7'],
    decisionCount: 4,
    avgGrade: 6.5,
    best: { handId: 'hand-7', decisionIndex: 0, street: 'flop', grade: 9, note: 'Thin value bet the flop.' },
    worst: validDecisionRef,
};
export const validSeriesDebrief = {
    cadence: 'perHand',
    units: [validDebriefUnit],
    overall: {
        handCount: 1,
        decisionCount: 4,
        avgGrade: 6.5,
        best: validDebriefUnit.best,
        worst: validDecisionRef,
    },
};
// ── exploit rules + range sets (D1's contracts) ─────────────────────────────────
// validExploitRule is R1 (OVERCALLER_THIN_VALUE) from ENGINE_DESIGN.md §6.3, verbatim in shape.
export const validExploitRule = {
    id: 'OVERCALLER_THIN_VALUE',
    version: 1,
    source: {
        framework: 'five-imbalances',
        note: 'over-calls ⇒ value thinner, size up; stations are price-insensitive',
    },
    priority: 40,
    confidence: 'high',
    when: {
        street: ['flop', 'turn', 'river'],
        villainImbalance: { bettingVolume: 'overCalls' },
        heroHandClass: { min: 'second_pair_good_kicker' },
        facing: ['check'],
    },
    effect: {
        action: { set: 'bet' },
        sizingIntent: { floor: 'twoThirds' },
        valueThreshold: 'thinner',
    },
    explain: {
        principle: 'Against players who call too much, bet thinner for value and size up, they pay with worse.',
        villainNote: 'The Sticker hates folding pairs; your second pair is a value hand here, not a check-back.',
    },
};
export const validExploitRuleTable = { version: 1, rules: [validExploitRule] };
// ── pro roster + stakes presets (D2's contracts) ────────────────────────────────
// archetypeId must resolve against a shipped archetype; blend mirrors ScenarioSpec villain blends.
export const validProProfile = {
    id: 'phil-ivey',
    name: 'Phil Ivey',
    archetypeId: 'surgeon',
    blend: [
        { archetypeId: 'surgeon', weight: 0.6 },
        { archetypeId: 'bully', weight: 0.4 },
    ],
    styleNote: 'Quiet, fearless, predatory; maximal pressure with minimal speech.',
    activityTier: 'selectiveLegend',
};
export const validProRoster = { version: 1, pros: [validProProfile] };
// The $1/$3 primary game (North Star §5); `stakes` drops directly into ScenarioSpec.stakes.
export const validStakesPreset = {
    id: '1_3',
    label: '$1/$3 NL',
    gameType: 'cash',
    stakes: { sb: 1, bb: 3, currency: 'USD' },
    tier: 'primary',
};
export const validStakesPresetTable = {
    version: 1,
    defaultPresetId: '1_3',
    presets: [validStakesPreset],
};
// id matches validArchetype.preflop.rangeSetId - the archetype↔range cross-reference D1 must keep consistent.
export const validRangeSet = {
    id: 'live_loose_9max',
    version: 'ranges:v1',
    tableSize: 9,
    source: 'Derived from public PokerCoaching/Little RFI charts; not a redistributed PDF.',
    ranges: { CO_RFI: '22+, ATs+, KQs, AJo+', BB_vs_BTN_3bet: 'JJ+, AKs, A5s' },
};
// ── read-axis contracts (D3's contracts) ────────────────────────────────────────
export const validReadLock = { state: 'lockedIn', revisionCount: 1, lockedAtDecisionIndex: 2 };
/** Guessed sticker with a tourist dash - both over-callers; the S9B partial-credit surface. */
export const validPlayerReadGuess = {
    seat: 2,
    guessedArchetypeId: 'sticker',
    blend: [
        { archetypeId: 'sticker', weight: 0.7 },
        { archetypeId: 'tourist', weight: 0.3 },
    ],
    imbalanceGuess: { bettingVolume: 'overCalls', elasticity: 'inelastic' },
    tags: ['limps in early', 'never folds a pair'],
    lock: validReadLock,
};
export const validTableReadGuess = {
    guessedTableType: 'loosePassive',
    notes: ['five or six to the flop', 'nobody re-raises'],
    lock: { state: 'lockedIn', revisionCount: 0 },
};
/** Painted grid keyed by canonical hand notation; an absent cell means weight 0. */
export const validRangeReadGuess = {
    seat: 2,
    street: 'flop',
    grid: { AA: 1, KK: 1, AKs: 0.5, '72o': 0 },
    lock: { state: 'open', revisionCount: 0 },
};
export const validReadVerdict = {
    dimension: 'player_profiling',
    correctness: 'partial',
    score: 6,
    errorCodes: ['MISREAD_ARCHETYPE'],
    groundTruth: 'sticker',
    note: 'Adjacent read: guessed tourist, actually sticker, both over-call, so partial credit.',
};
export const validReadGrade = {
    players: [validReadVerdict],
    table: {
        dimension: 'table_reading',
        correctness: 'correct',
        score: 10,
        errorCodes: [],
        groundTruth: 'loosePassive',
    },
    ranges: [{ dimension: 'player_profiling', correctness: 'wrong', score: 3, errorCodes: ['MISREAD_VILLAIN_RANGE'] }],
    axisBScore: 6,
};
/** archetypeId must resolve against a shipped archetype; mirrors table-types.v1.json rows. */
export const validTableTypePreset = {
    tableType: 'callingStation',
    label: 'Calling-station table',
    description: 'Players call down light; almost never bluff, bet bigger for value.',
    archetypeMix: [
        { archetypeId: 'sticker', weight: 5 },
        { archetypeId: 'sheriff', weight: 2 },
    ],
    dynamics: { limpiness: 0.6, aggression: 0.15, stickiness: 0.95, avgPlayersToFlop: 4 },
    sizing: { defaultOpenIntent: 'threeQuarters', valueBias: 0.95, bluffBias: 0.05 },
    commonStakes: ['1_3'],
    source: 'Field guide §12.3 (calling-station table).',
};
export const validTableTypePresetTable = { version: 1, presets: [validTableTypePreset] };
/** ScenarioSpec carrying the D3 read-axis shell fields - proves they parse additively. */
export const validScenarioSpecWithRead = {
    ...validScenarioSpec,
    tableType: 'loosePassive',
    learningLayer: 'guided',
    trainingContext: 'local',
};
//# sourceMappingURL=fixtures.js.map